// Bank rules (BRIEF.md section 9): match on description (contains, regex), amount range and
// direction; the first active match by priority wins. Rules auto-post.
import { normaliseDescription } from './bank-statement.js';

export interface RuleMatch {
  /** Every phrase must appear (case and punctuation insensitive). */
  readonly contains?: readonly string[];
  readonly regex?: string;
  readonly direction?: 'in' | 'out';
  /** Inclusive bounds on the absolute amount, minor units as strings. */
  readonly minAmount?: string;
  readonly maxAmount?: string;
}

export interface MatchableRule {
  readonly id: string;
  readonly priority: number;
  readonly match: RuleMatch;
}

export interface MatchableLine {
  readonly amount: bigint;
  readonly description: string;
}

export function ruleMatches(rule: MatchableRule, line: MatchableLine): boolean {
  const { match } = rule;
  if (line.amount === 0n) return false;
  if (match.direction === 'in' && line.amount < 0n) return false;
  if (match.direction === 'out' && line.amount > 0n) return false;
  const abs = line.amount < 0n ? -line.amount : line.amount;
  if (match.minAmount !== undefined && abs < BigInt(match.minAmount)) return false;
  if (match.maxAmount !== undefined && abs > BigInt(match.maxAmount)) return false;
  const text = normaliseDescription(line.description);
  if (match.contains?.some((phrase) => !text.includes(normaliseDescription(phrase)))) return false;
  if (match.regex !== undefined) {
    try {
      if (!new RegExp(match.regex, 'i').test(line.description)) return false;
    } catch {
      return false;
    }
  }
  return (
    (match.contains?.length ?? 0) > 0 || match.regex !== undefined || match.direction !== undefined
  );
}

export function firstMatchingRule<R extends MatchableRule>(
  rules: readonly R[],
  line: MatchableLine,
): R | undefined {
  return [...rules].sort((a, b) => a.priority - b.priority).find((r) => ruleMatches(r, line));
}

/** Built-in rules (M2 plan D4, D5). Product "Shared" for fees that serve every product. */
export const BUILT_IN_RULES = [
  {
    name: 'Stripe payouts',
    priority: 10,
    match: { contains: ['STRIPE'], direction: 'in' },
    category: 'Stripe payout',
  },
  {
    name: 'International transaction fees',
    priority: 10,
    match: { contains: ['INT TRAN FEE'], direction: 'out' },
    category: 'Bank fees',
    product: 'Shared',
  },
] as const satisfies readonly {
  name: string;
  priority: number;
  match: RuleMatch;
  category: string;
  product?: string;
}[];

/** A merchant key for "always do this": the description without card references and numbers. */
export function merchantKey(description: string): string {
  const cleaned = description
    .replace(/^VISA\s*-\s*/i, '')
    .replace(/#\d+.*$/, '')
    .replace(/\(Ref\.[^)]*\)/gi, '')
    .replace(/FRGN AMT.*$/i, '')
    .replace(/\b[A-Z]{2}$/, '');
  const words = normaliseDescription(cleaned)
    .split(' ')
    .filter((w) => w.length > 1 && !/\d/.test(w));
  return words.slice(0, 3).join(' ');
}
